import { useEffect, useState } from 'react';
import { useAdminStore, type AdminUser } from '../stores/adminStore';
import { useConfirmModal } from '../components/ConfirmModal';
import { formStyles } from '../components/formStyles';
import { theme } from '../theme';

const PLANS = ['free', 'indie', 'studio', 'enterprise'];

const styles = {
  container: { maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' },
  title: { fontSize: '24px', fontWeight: 700, color: theme.textPrimary, marginBottom: '16px' },
  section: {
    background: theme.bgPanel,
    border: `1px solid ${theme.border}`,
    borderRadius: theme.radiusLg,
    padding: '20px',
    marginBottom: '24px',
    overflowX: 'auto' as const,
  },
  heading: { fontSize: '16px', fontWeight: 600, color: theme.textPrimary, marginBottom: '12px' },
  totals: { display: 'flex', gap: '32px', flexWrap: 'wrap' as const, color: theme.textSecondary },
  total: { fontSize: '20px', fontWeight: 600, color: theme.textPrimary },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '13px' },
  th: {
    textAlign: 'left' as const,
    color: theme.textMuted,
    fontWeight: 500,
    padding: '6px 8px',
    borderBottom: `1px solid ${theme.border}`,
    whiteSpace: 'nowrap' as const,
  },
  td: {
    color: theme.textSecondary,
    padding: '6px 8px',
    borderBottom: `1px solid ${theme.border}`,
    verticalAlign: 'top' as const,
  },
  small: {
    padding: '4px 10px',
    borderRadius: theme.radiusSm,
    border: `1px solid ${theme.borderStrong}`,
    background: 'transparent',
    color: theme.textSecondary,
    cursor: 'pointer',
    fontSize: '12px',
    marginRight: '6px',
  },
  danger: {
    padding: '4px 10px',
    borderRadius: theme.radiusSm,
    border: 'none',
    background: theme.danger,
    color: theme.accentText,
    cursor: 'pointer',
    fontSize: '12px',
  },
  select: {
    background: theme.bgInput,
    color: theme.textPrimary,
    border: `1px solid ${theme.border}`,
    borderRadius: theme.radiusSm,
    fontSize: '12px',
    padding: '2px 4px',
  },
  inlineInput: { ...formStyles.input, width: '180px', padding: '4px 8px', fontSize: '12px' },
  row: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' },
  notice: { color: theme.warning, fontSize: '13px', marginBottom: '12px' },
};

function mb(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

function day(ms: number | null): string {
  return ms == null ? '' : new Date(ms).toISOString().slice(0, 10);
}

function statusText(user: AdminUser): string {
  if (user.status === 'suspended') return `suspended: ${user.suspendedReason ?? ''}`;
  if (user.status === 'deletion-pending') return 'deletion pending';
  return 'active';
}

export function Admin() {
  const {
    summary,
    users,
    projects,
    invites,
    notAdmin,
    error,
    lastInvite,
    fetchAll,
    suspend,
    unsuspend,
    setPlan,
    sendInvite,
    revokeInvite,
    reports,
    showClosedReports,
    knownIssues,
    loadReports,
    setReportClosed,
    loadKnownIssues,
    saveKnownIssue,
    deleteKnownIssue,
  } = useAdminStore();
  const [confirm, confirmModal] = useConfirmModal();
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [inviteEmail, setInviteEmail] = useState('');
  const [actionError, setActionError] = useState('');
  const [issueDraft, setIssueDraft] = useState<{ id?: string; title: string; body: string }>({
    title: '',
    body: '',
  });

  useEffect(() => {
    fetchAll();
    loadReports(false);
    loadKnownIssues();
  }, [fetchAll, loadReports, loadKnownIssues]);

  const act = async (fn: () => Promise<void>) => {
    setActionError('');
    try {
      await fn();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'That did not work');
    }
  };

  const onSuspend = async (user: AdminUser) => {
    const reason = (reasons[user.id] ?? '').trim();
    if (!reason) {
      setActionError(`Write a reason before suspending ${user.username}`);
      return;
    }
    const ok = await confirm({
      title: `Suspend ${user.username}?`,
      message: `They are signed out, their running games stop, and they cannot log in until you unsuspend them. Reason: ${reason}`,
      confirmLabel: 'Suspend',
    });
    if (ok) await act(() => suspend(user.id, reason));
  };

  if (notAdmin) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Not found</h1>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {confirmModal}
      <h1 style={styles.title}>Admin</h1>
      {error && <div style={formStyles.error}>{error}</div>}
      {actionError && <div style={formStyles.error}>{actionError}</div>}

      {summary && (
        <div style={styles.section}>
          <div style={styles.totals}>
            <div>
              <div style={styles.total}>{summary.users}</div>users
            </div>
            <div>
              <div style={styles.total}>
                {summary.projects.live} / {summary.projects.trash}
              </div>
              games live / in trash
            </div>
            <div>
              <div style={styles.total}>{summary.runningGames}</div>running
            </div>
            <div>
              <div style={styles.total}>{mb(summary.storageBytes.projects)}</div>games on disk
            </div>
            <div>
              <div style={styles.total}>{mb(summary.storageBytes.restorePoints)}</div>restore points
              on disk
            </div>
          </div>
        </div>
      )}

      <div style={styles.section}>
        <div style={styles.heading}>Reports</div>
        <label style={{ ...styles.row, color: theme.textSecondary, fontSize: '13px' }}>
          <input
            type="checkbox"
            checked={showClosedReports}
            onChange={(e) => loadReports(e.target.checked)}
          />
          Show closed
        </label>
        {reports.length === 0 && (
          <div style={{ color: theme.textMuted, fontSize: '13px' }}>No reports.</div>
        )}
        <table style={styles.table}>
          <tbody>
            {reports.map((r) => (
              <tr key={r.id} style={{ opacity: r.closedAt ? 0.6 : 1 }}>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                  <strong>{r.kind === 'bug' ? 'Bug' : 'Idea'}</strong>
                  <br />
                  {day(r.createdAt)}
                </td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                  {r.username}
                  <br />
                  <a href={`mailto:${r.email}`} style={{ color: theme.accent }}>
                    {r.email}
                  </a>
                </td>
                <td style={styles.td}>
                  <div style={{ whiteSpace: 'pre-wrap', color: theme.textPrimary }}>
                    {r.message}
                  </div>
                  <div style={{ color: theme.textMuted, fontSize: '12px', marginTop: '4px' }}>
                    {r.projectName ?? 'no project'} | {r.app} {r.page}
                    {r.editorTab ? ` | tab ${r.editorTab}` : ''} | {r.viewport} | {r.userAgent}
                  </div>
                </td>
                <td style={styles.td}>
                  <button
                    style={styles.small}
                    onClick={() => act(() => setReportClosed(r.id, !r.closedAt))}
                  >
                    {r.closedAt ? 'Reopen' : 'Close'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.section}>
        <div style={styles.heading}>
          Known issues{' '}
          <a
            href="/known-issues"
            style={{ color: theme.accent, fontSize: '12px', fontWeight: 400 }}
          >
            public page
          </a>
        </div>
        <div style={{ ...styles.row, alignItems: 'flex-start' }}>
          <input
            style={{ ...formStyles.input, width: '280px' }}
            placeholder="Title"
            maxLength={120}
            value={issueDraft.title}
            onChange={(e) => setIssueDraft({ ...issueDraft, title: e.target.value })}
          />
          <textarea
            style={{ ...formStyles.input, flex: 1, minHeight: '40px', resize: 'vertical' }}
            placeholder="What happens, and any workaround"
            maxLength={2000}
            value={issueDraft.body}
            onChange={(e) => setIssueDraft({ ...issueDraft, body: e.target.value })}
          />
          <button
            style={{ ...formStyles.btn, width: 'auto', marginTop: 0, padding: '10px 20px' }}
            onClick={() =>
              act(async () => {
                await saveKnownIssue(issueDraft);
                setIssueDraft({ title: '', body: '' });
              })
            }
          >
            {issueDraft.id ? 'Save' : 'Post'}
          </button>
          {issueDraft.id && (
            <button style={styles.small} onClick={() => setIssueDraft({ title: '', body: '' })}>
              Cancel
            </button>
          )}
        </div>
        <table style={styles.table}>
          <tbody>
            {knownIssues.map((issue) => (
              <tr key={issue.id}>
                <td style={styles.td}>
                  <strong style={{ color: theme.textPrimary }}>{issue.title}</strong>
                  {issue.body && <div style={{ whiteSpace: 'pre-wrap' }}>{issue.body}</div>}
                </td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                  {issue.status === 'fixed' && issue.fixedAt
                    ? `fixed ${day(issue.fixedAt)}`
                    : 'open'}
                </td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                  <button
                    style={styles.small}
                    onClick={() =>
                      setIssueDraft({ id: issue.id, title: issue.title, body: issue.body })
                    }
                  >
                    Edit
                  </button>
                  <button
                    style={styles.small}
                    onClick={() =>
                      act(() =>
                        saveKnownIssue({
                          id: issue.id,
                          status: issue.status === 'open' ? 'fixed' : 'open',
                        })
                      )
                    }
                  >
                    {issue.status === 'open' ? 'Mark fixed' : 'Reopen'}
                  </button>
                  <button
                    style={styles.danger}
                    onClick={async () => {
                      const ok = await confirm({
                        title: `Delete "${issue.title}"?`,
                        message: 'It disappears from the public known-issues page too.',
                        confirmLabel: 'Delete',
                      });
                      if (ok) await act(() => deleteKnownIssue(issue.id));
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.section}>
        <div style={styles.heading}>Users</div>
        <table style={styles.table}>
          <thead>
            <tr>
              {[
                'User',
                'Plan',
                'Verified',
                'Joined',
                'Games (live / trash)',
                'Storage',
                'Restore points',
                'Running',
                'Status',
                '',
              ].map((h) => (
                <th key={h} style={styles.th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td style={styles.td}>
                  {u.username}
                  {u.role === 'admin' ? ' (admin)' : ''}
                  <br />
                  <span style={{ color: theme.textMuted }}>{u.email}</span>
                </td>
                <td style={styles.td}>
                  <select
                    style={styles.select}
                    value={u.plan}
                    onChange={(e) => act(() => setPlan(u.id, e.target.value))}
                  >
                    {PLANS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={styles.td}>{u.emailVerified ? 'yes' : 'no'}</td>
                <td style={styles.td}>{day(u.createdAt)}</td>
                <td style={styles.td}>
                  {u.usage.projects.used} / {u.projectsTrashed} (max {u.usage.projects.limit})
                </td>
                <td style={styles.td}>
                  {mb(u.usage.storageBytes.used)} of {mb(u.usage.storageBytes.limit)}
                </td>
                <td style={styles.td}>{mb(u.usage.storageBytes.restorePoints)}</td>
                <td style={styles.td}>
                  {u.usage.runningGames.used} of {u.usage.runningGames.limit}
                </td>
                <td style={styles.td}>{statusText(u)}</td>
                <td style={styles.td}>
                  {u.role === 'admin' ? null : u.status === 'suspended' ? (
                    <button style={styles.small} onClick={() => act(() => unsuspend(u.id))}>
                      Unsuspend
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input
                        style={styles.inlineInput}
                        placeholder="Reason"
                        maxLength={200}
                        value={reasons[u.id] ?? ''}
                        onChange={(e) => setReasons({ ...reasons, [u.id]: e.target.value })}
                      />
                      <button style={styles.danger} onClick={() => onSuspend(u)}>
                        Suspend
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.section}>
        <div style={styles.heading}>Projects</div>
        <table style={styles.table}>
          <thead>
            <tr>
              {['Game', 'Owner', 'Status', 'Storage', 'Restore points', 'Created', 'Trashed'].map(
                (h) => (
                  <th key={h} style={styles.th}>
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.id}>
                <td style={styles.td}>
                  {p.name}
                  <br />
                  <span style={{ color: theme.textMuted }}>{p.slug}</span>
                </td>
                <td style={styles.td}>{p.ownerUsername}</td>
                <td style={styles.td}>{p.status}</td>
                <td style={styles.td}>{mb(p.storageBytes)}</td>
                <td style={styles.td}>
                  {p.restorePoints.count} ({mb(p.restorePoints.bytes)})
                </td>
                <td style={styles.td}>{day(p.createdAt)}</td>
                <td style={styles.td}>{day(p.deletedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.section}>
        <div style={styles.heading}>Invites</div>
        <div style={styles.row}>
          <input
            style={{ ...formStyles.input, width: '280px' }}
            type="email"
            placeholder="Email to invite"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <button
            style={{ ...formStyles.btn, width: 'auto', marginTop: 0, padding: '10px 20px' }}
            onClick={() =>
              act(async () => {
                await sendInvite(inviteEmail.trim());
                setInviteEmail('');
              })
            }
          >
            Send invite
          </button>
        </div>
        {lastInvite && !lastInvite.mailed && (
          <div style={styles.notice}>
            The email to {lastInvite.email} did not send. Pass this code on by hand:{' '}
            <strong>{lastInvite.code}</strong>
          </div>
        )}
        <table style={styles.table}>
          <thead>
            <tr>
              {['Email', 'Status', 'Sent', 'Expires', 'Redeemed by', ''].map((h) => (
                <th key={h} style={styles.th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invites.map((inv, i) => (
              <tr key={`${inv.email}-${inv.createdAt}-${i}`}>
                <td style={styles.td}>{inv.email}</td>
                <td style={styles.td}>{inv.status}</td>
                <td style={styles.td}>{day(inv.createdAt)}</td>
                <td style={styles.td}>{day(inv.expiresAt)}</td>
                <td style={styles.td}>{inv.redeemedByUsername ?? ''}</td>
                <td style={styles.td}>
                  {(inv.status === 'open' || inv.status === 'expired') && (
                    <button style={styles.small} onClick={() => act(() => sendInvite(inv.email))}>
                      Resend
                    </button>
                  )}
                  {inv.status === 'open' && (
                    <button style={styles.small} onClick={() => act(() => revokeInvite(inv.email))}>
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
